import { type FC, useCallback, useMemo, type ChangeEvent, useEffect, useState } from 'react';

import { type SelectValue } from '@mui/base';
import { type SelectChangeEventType } from '@mui/base/useSelect/useSelect.types';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { InputAdornmentComponent } from '@vakantiesnl/components/src/atoms/InputAdornment';
import { CheckboxFormField } from '@vakantiesnl/components/src/molecules/CheckboxFormField';
import { type SelectFormFieldProps } from '@vakantiesnl/components/src/molecules/SelectFormField';
import { SelectFormField } from '@vakantiesnl/components/src/molecules/SelectFormField/OldSelectFormField';
import { type TextfieldComponentProps } from '@vakantiesnl/components/src/molecules/TextField';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { type TripFilterContextProps } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { type Booking, type Search } from '@vakantiesnl/types/src';
import { ActionLabels } from '@vakantiesnl/types/src/booking';
import { omit } from 'lodash';

import configValues from '@ConfigValues';

import { useCustomStyles } from './FlightFilterMenu.styles';

type Props = {
	flightFilters: Booking.FlightFilters;
	setFilters: (filter: Booking.FlightFilters) => void;
	accommodation: Search.FullAccommodationItem;
	updateSelectedDirectOnly: TripFilterContextProps['dispatch']['updateSelectedDirectOnly'];
	nrOfFlightFilters: number;
	actionLabel: string;
	setActionLabel: (label: string) => void;
};

export const FlightFilterMenuVariantB: FC<Props> = ({
	flightFilters,
	setFilters,
	accommodation,
	updateSelectedDirectOnly,
	nrOfFlightFilters,
	actionLabel,
	setActionLabel,
}) => {
	const [initialFlightSelection, setInitialFlightSelection] = useState(true);
	const [selectedInboundPeriod, setSelectedInboundPeriod] = useState<string>();
	const [selectedOutboundPeriod, setSelectedOutboundPeriod] = useState<string>();
	const { classes } = useCustomStyles();
	const microcopies = useMicroCopyContext();

	const options = useMemo(
		() =>
			configValues.flightFilterValues?.map((option) => ({
				label: option.daypart,
				value: `${option.from}-${option.to}`,
				from: option.from,
				to: option.to,
			})),
		[],
	);

	useEffect(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ flightFiltersGTM }) => {
				track(
					flightFiltersGTM({
						outboundFlight: selectedOutboundPeriod,
						inboundFlight: selectedInboundPeriod,
						includesTransfer: flightFilters.directOnly,
						heliosId: accommodation.heliosId,
						noOfFilters: nrOfFlightFilters,
						initialLoad: initialFlightSelection,
						actionLabel: actionLabel,
					}),
				);
			}),
		);
	}, [
		flightFilters,
		initialFlightSelection,
		accommodation.giataId,
		accommodation.heliosId,
		selectedInboundPeriod,
		selectedOutboundPeriod,
		nrOfFlightFilters,
		actionLabel,
	]);

	const defaultOption = useMemo(
		() => ({
			label: microcopies['filters.noPreference'],
			value: '',
			from: undefined,
			to: undefined,
		}),
		[microcopies],
	);

	const InputProps: TextfieldComponentProps['InputProps'] = useMemo(
		() => ({
			startAdornment: (
				<InputAdornmentComponent position="start" type="icon">
					<Icon type="time" />
				</InputAdornmentComponent>
			),
		}),
		[],
	);

	const onFlightFilterChange = useCallback(
		(newFlightFilter: Booking.FlightFilters) => {
			/** Update filters in Redux and in local state */
			setFilters(newFlightFilter);

			/** Update local state helper for event tracking */
			if (initialFlightSelection) setInitialFlightSelection(false);
		},
		[initialFlightSelection, setFilters],
	);

	const onChangeDirectOnly = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setActionLabel(ActionLabels.directOnly);
			onFlightFilterChange({ ...flightFilters, directOnly: e.target.checked });
			updateSelectedDirectOnly(e.target.checked);
		},
		[flightFilters, onFlightFilterChange, updateSelectedDirectOnly, setActionLabel],
	);

	/** On change set the flight filters
	 * @if there's a selected time value, split it and set the values ${from} and ${to} in the ${outboundFlight} flight filters
	 * @else remove the ${outboundFlight} filter so the value falls back to the default
	 */
	const onChangeOutboundFlight: SelectFormFieldProps<string, false>['onChange'] = useCallback(
		(_e: SelectChangeEventType, selectedValue: SelectValue<string, false>) => {
			setActionLabel(ActionLabels.outbound);
			setSelectedOutboundPeriod(options.find((option) => option.value === selectedValue)?.label);
			if (selectedValue && selectedValue.split('-')) {
				const from = selectedValue.split('-')[0];
				const to = selectedValue.split('-')[1];
				onFlightFilterChange({ ...flightFilters, outboundFlight: { from, to } });
			} else onFlightFilterChange(omit(flightFilters, 'outboundFlight'));
		},
		[flightFilters, onFlightFilterChange, options, setActionLabel],
	);

	/** On change set the flight filters
	 * @if there's a selected time value, split it and set the values ${from} and ${to} in the ${inboundFlight} flight filters
	 * @else remove the ${inboundFlight} filter so the value falls back to the default
	 */
	const onChangeInboundFlight: SelectFormFieldProps<string, false>['onChange'] = useCallback(
		(_e: SelectChangeEventType, selectedValue: SelectValue<string, false>) => {
			setActionLabel(ActionLabels.inbound);
			setSelectedInboundPeriod(options.find((option) => option.value === selectedValue)?.label);
			if (selectedValue && selectedValue.split('-')) {
				const from = selectedValue.split('-')[0];
				const to = selectedValue.split('-')[1];
				onFlightFilterChange({ ...flightFilters, inboundFlight: { from, to } });
			} else onFlightFilterChange(omit(flightFilters, 'inboundFlight'));
		},
		[flightFilters, onFlightFilterChange, options, setActionLabel],
	);

	/** Prepend the default option to the configuration options */
	const mappedOptions = useMemo(() => [defaultOption, ...options], [defaultOption, options]);

	return (
		<div className={classes.flightFilterMenuContainer}>
			<div className={classes.selectContainer}>
				<SelectFormField
					microCopies={microcopies}
					label={microcopies['common.outbound_flight']}
					InputProps={InputProps}
					options={mappedOptions}
					dataCy="outbound-flight-selector"
					placeholder={microcopies['filters.noPreference']}
					defaultValue={mappedOptions.find((option) => option.value === '')?.value}
					value={
						mappedOptions.find(
							(option) =>
								option.to === flightFilters?.outboundFlight?.to &&
								option.from === flightFilters?.outboundFlight?.from,
						)?.value
					}
					multiple={false}
					onChange={onChangeOutboundFlight}
				/>
				<SelectFormField
					microCopies={microcopies}
					label={microcopies['common.inbound_flight']}
					InputProps={InputProps}
					options={mappedOptions}
					dataCy="inbound-flight-selector"
					placeholder={microcopies['filters.noPreference']}
					defaultValue={mappedOptions.find((option) => option.value === '')?.value}
					value={
						mappedOptions.find(
							(option) =>
								option.to === flightFilters?.inboundFlight?.to &&
								option.from === flightFilters?.inboundFlight?.from,
						)?.value
					}
					onChange={onChangeInboundFlight}
				/>
			</div>
			<CheckboxFormField
				data-cy="direct-only-selector"
				label={microcopies['filters.noStopovers']}
				checked={flightFilters.directOnly}
				onChange={onChangeDirectOnly}
			/>
		</div>
	);
};
